import type {
  CashHandHistoryEntry,
  CurrencyOption,
  HistoryGameType,
} from '../../History/Type/HistoryTypes';
import { COLORS, type CurrencyTypeSelection } from './HistoryConstants';

// ─── Date Helpers ────────────────────────────────────────────────────────────

export const parseDate = (value?: string | number): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getDateKey = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export const formatDayLabel = (date: Date | null, todayKey: string, yesterdayKey: string): string => {
  if (!date) return 'Unknown date';
  const key = getDateKey(date);
  if (key === todayKey) return 'Today';
  if (key === yesterdayKey) return 'Yesterday';
  const now = new Date();
  const includeYear = date.getFullYear() !== now.getFullYear();
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: includeYear ? 'numeric' : undefined,
  });
};

export const formatMonthLabel = (date: Date | null): string => {
  if (!date) return 'Unknown month';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const formatDateTime = (value?: string | number): string => {
  const date = parseDate(value);
  if (!date) return '\u2014';
  const y = date.getFullYear();
  const m = date.toLocaleString('en-US', { month: 'short' });
  const d = date.getDate();
  const t = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${y}, ${m} ${d} ${t}`;
};

export const formatTimeOnly = (value?: string | number): string => {
  const date = parseDate(value);
  if (!date) return '\u2014';
  return date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit' });
};

// ─── Amount Formatting ───────────────────────────────────────────────────────

export const formatAmount = (value?: number, currency?: string): string => {
  if (typeof value !== 'number') return '\u2014';
  const negative = value < 0;
  const absFormatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Math.abs(value)
  );
  if (!currency) return negative ? `-${absFormatted}` : absFormatted;
  const upper = currency.toUpperCase();
  if (['USD', 'USDT', 'USDC'].includes(upper)) {
    return negative ? `-$${absFormatted}` : `$${absFormatted}`;
  }
  return negative ? `-${absFormatted} ${upper}` : `${absFormatted} ${upper}`;
};

export const formatNetAmount = (entry: CashHandHistoryEntry): number | null => {
  const direct = entry.user?.netAmount;
  if (typeof direct === 'number') return direct;
  const before = entry.user?.balanceBefore;
  const after = entry.user?.balanceAfter;
  if (typeof before === 'number' && typeof after === 'number') return after - before;
  return null;
};

// ─── Game Type ───────────────────────────────────────────────────────────────

export const formatGameTypeShort = (gameType?: HistoryGameType): string => {
  if (!gameType) return '\u2014';
  const normalized = gameType.toString().toLowerCase();
  if (normalized === 'texas') return 'Texas';
  if (normalized === 'aof') return 'AoF';
  return gameType.toString();
};

export const getGameTypeColor = (gameType?: HistoryGameType): { color: string; bg: string } => {
  if (!gameType) return { color: COLORS.textMuted, bg: 'transparent' };
  const normalized = gameType.toString().toLowerCase();
  if (normalized === 'aof') return { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' };
  return { color: '#5b9bf5', bg: 'rgba(91, 155, 245, 0.12)' };
};

export const formatTournamentStatus = (status?: string): { label: string; color: string; bg: string } => {
  if (!status) return { label: '\u2014', color: COLORS.textMuted, bg: 'transparent' };
  const normalized = status.toUpperCase();
  if (normalized === 'COMPLETED') return { label: 'Completed', color: COLORS.completed, bg: COLORS.completedBg };
  if (normalized === 'CANCELLED' || normalized === 'CANCELED') return { label: 'Cancelled', color: COLORS.cancelled, bg: COLORS.cancelledBg };
  if (normalized === 'RUNNING') return { label: 'Running', color: COLORS.running, bg: COLORS.runningBg };
  return { label: status, color: COLORS.textMuted, bg: 'transparent' };
};

// ─── Action / Round Labels ───────────────────────────────────────────────────

const ROUND_LABEL_MAP: Record<string, string> = {
  preFlop: 'Preflop',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
  dealing: 'Preflop',
  showdown: 'Showdown',
  ante: 'Ante',
  rake: 'Rake',
  prize: 'Prize',
  refund: 'Refund',
};

/** Rounds that are system/audit entries, not player action rounds. */
const SYSTEM_ROUNDS = new Set(['rake', 'prize', 'refund']);

/** Actions that carry no meaningful amount (display label only). */
const NO_AMOUNT_ACTIONS = new Set(['fold', 'check', 'show', 'muck']);

export const isSystemRound = (roundName?: string): boolean =>
  SYSTEM_ROUNDS.has(roundName ?? '');

export const actionHasAmount = (action?: string): boolean =>
  !NO_AMOUNT_ACTIONS.has(action ?? '');

export const formatActionLabel = (value?: string): string => {
  if (!value) return '\u2014';
  const normalized = value.toString().replace(/_/g, ' ').toLowerCase();
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatRoundLabel = (value?: string): string => {
  if (!value) return 'Unknown Round';
  return ROUND_LABEL_MAP[value] ?? value.toString().replace(/_/g, ' ').toUpperCase();
};

// ─── Currency Options ────────────────────────────────────────────────────────

export const buildCurrencyOptionValue = (option: CurrencyOption): string =>
  `${option.currency}__${option.currencyType}`;

export const parseCurrencyOptionValue = (
  value: string
): { currency: string; currencyType: CurrencyTypeSelection } | null => {
  if (!value) return null;
  const [currency, currencyType] = value.split('__');
  if (!currency || !currencyType) return null;
  return { currency, currencyType: currencyType as CurrencyTypeSelection };
};

export const formatCurrencyOptionLabel = (option: CurrencyOption): string => {
  const typeLabel = option.currencyType.toString().toUpperCase();
  const baseLabel = option.baseCurrency ? ` \u2022 Base ${option.baseCurrency}` : '';
  return `${option.currency} (${typeLabel})${baseLabel}`;
};

// ─── Card Display ────────────────────────────────────────────────────────────

export const getCardColor = (card: string): string => {
  if (!card || card.length < 2) return COLORS.textPrimary;
  const suit = card[card.length - 1].toLowerCase();
  if (suit === 'h') return COLORS.cardSuit.hearts;
  if (suit === 'd') return COLORS.cardSuit.diamonds;
  if (suit === 'c') return COLORS.cardSuit.clubs;
  if (suit === 's') return COLORS.cardSuit.spades;
  return COLORS.textPrimary;
};

export const getSuitSymbol = (suit: string): string => {
  const s = suit.toLowerCase();
  if (s === 'h') return '\u2665';
  if (s === 'd') return '\u2666';
  if (s === 'c') return '\u2663';
  if (s === 's') return '\u2660';
  return suit;
};

export const formatCardDisplay = (card: string): { rank: string; suit: string } => {
  if (!card || card.length < 2) return { rank: card || '', suit: '' };
  const rank = card.slice(0, -1).toUpperCase();
  const suit = getSuitSymbol(card[card.length - 1]);
  return { rank, suit };
};
